import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import PhotoUpload from './PhotoUpload'

// ANU-88 — Target ID (T-ID) section.
// Two modes:
//   editMode=true  — collapsible form for bounty/FRAGO builders (FLARE green)
//   editMode=false — read-only display for BountyDetail/FragoDetail (dossier sepia)

// ── Edit mode ─────────────────────────────────────────────────────────────

const CE = {
  label:   { color: '#4aaa5a', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4, display: 'block', fontFamily: "'JetBrains Mono', monospace" },
  input:   { width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '8px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, outline: 'none' },
  section: { background: 'rgba(0,255,102,.03)', border: '1px solid rgba(0,255,102,.15)', padding: '12px 14px', marginTop: 8 },
  header:  { fontFamily: "'VT323', monospace", fontSize: 13, color: '#9aff9a', letterSpacing: '0.12em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  toggle:  (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', fontFamily: "'VT323', monospace", fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', background: on ? 'rgba(0,255,102,.15)' : 'rgba(0,20,8,.4)', color: on ? '#00ff66' : '#4aaa5a', border: on ? '1px solid rgba(0,255,102,.4)' : '1px solid rgba(0,255,102,.18)' }),
}

// Inline redacted bar for the live preview card (sepia/dossier theme).
function RedactedBar({ width = 60 }) {
  return (
    <span style={{ background: '#1a1a1a', color: 'transparent', display: 'inline-block', minWidth: width, height: '0.85em', verticalAlign: 'middle', borderRadius: 1 }}>&nbsp;</span>
  )
}

// Live preview of the T-ID card in dossier style, driven by form state.
// Each field shows its real value once filled; shows a redacted bar when empty.
function TIDLivePreview({ tid }) {
  const { callsign, displayName, description, lastKnown, photoSignedUrl } = tid
  const hasCallsign    = callsign.trim().length > 0
  const hasDisplayName = displayName.trim().length > 0
  const hasDescription = description.trim().length > 0
  const hasLastKnown   = lastKnown.trim().length > 0

  return (
    <div style={{ background: '#e8dec3', border: '1px solid rgba(120,100,60,.35)', padding: 12, marginTop: 10, display: 'flex', gap: 12 }}>
      {/* Photo slot */}
      <div style={{ flexShrink: 0 }}>
        {photoSignedUrl ? (
          <div style={{ width: 72, height: 90, border: '1px solid #7a7058', overflow: 'hidden' }}>
            <img src={photoSignedUrl} alt="Target" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : (
          <div style={{ width: 72, height: 90, background: '#1a1a1a', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <span style={{ fontFamily: "'Stardos Stencil', 'Special Elite', serif", fontSize: 7, color: '#444', letterSpacing: 2, transform: 'rotate(-30deg)', position: 'absolute' }}>CLASSIFIED</span>
          </div>
        )}
      </div>

      {/* Text fields */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Stardos Stencil', 'Special Elite', serif", fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', color: '#1f1d18', marginBottom: 5 }}>
          {hasCallsign ? callsign : <RedactedBar width={80} />}
          {hasDisplayName && (
            <span style={{ fontFamily: "'Special Elite', 'Courier Prime', monospace", fontSize: 11, color: '#4a4636', marginLeft: 8, fontWeight: 400 }}>· {displayName}</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#4a4636', lineHeight: 1.5, marginBottom: 3 }}>
          {hasDescription ? description : <RedactedBar width={120} />}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginTop: 4 }}>
          <span style={{ fontFamily: "'Courier Prime', monospace", color: '#7a7058', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: 52, flexShrink: 0 }}>LAST SEEN</span>
          {hasLastKnown ? (
            <span style={{ fontSize: 11, color: '#4a4636' }}>{lastKnown}</span>
          ) : (
            <RedactedBar width={100} />
          )}
        </div>
      </div>
    </div>
  )
}

export function TIDEditSection({ tid, onChange, eventId, uploadFolder, disabled = false }) {
  if (!tid) return null
  const { enabled, callsign, displayName, description, lastKnown, photoStoragePath } = tid

  return (
    <div style={CE.section}>
      <div style={CE.header} onClick={() => !disabled && onChange({ ...tid, enabled: !enabled })}>
        <span>§ TARGET ID (T-ID)</span>
        <button
          type="button"
          style={CE.toggle(enabled)}
          disabled={disabled}
          onClick={e => { e.stopPropagation(); onChange({ ...tid, enabled: !enabled }) }}
        >
          {enabled ? '✓ ENABLED' : '○ OPTIONAL'}
        </button>
      </div>

      {enabled && (
        <>
        <TIDLivePreview tid={tid} />
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={CE.label}>Callsign *</label>
              <input
                style={CE.input}
                value={callsign}
                onChange={e => onChange({ ...tid, callsign: e.target.value })}
                placeholder="Ghost"
                disabled={disabled}
              />
            </div>
            <div>
              <label style={CE.label}>Display Name</label>
              <input
                style={CE.input}
                value={displayName}
                onChange={e => onChange({ ...tid, displayName: e.target.value })}
                placeholder="Alexei Volkov"
                disabled={disabled}
              />
            </div>
          </div>
          <div>
            <label style={CE.label}>Description / What to Look For *</label>
            <input
              style={CE.input}
              value={description}
              onChange={e => onChange({ ...tid, description: e.target.value })}
              placeholder="HVT, downed pilot, tan flight suit"
              disabled={disabled}
            />
          </div>
          <div>
            <label style={CE.label}>Last Known Location / Activity</label>
            <input
              style={CE.input}
              value={lastKnown}
              onChange={e => onChange({ ...tid, lastKnown: e.target.value })}
              placeholder="Last seen south treeline near Obj Bravo"
              disabled={disabled}
            />
          </div>
          <div>
            <label style={CE.label}>Target Photo (Optional)</label>
            <PhotoUpload
              folder={uploadFolder}
              label="Add Target Photo"
              existingUrl={tid.photoSignedUrl || null}
              disabled={disabled}
              onUpload={(signedUrl, path) => onChange({ ...tid, photoStoragePath: path, photoSignedUrl: signedUrl })}
            />
          </div>
        </div>
        </>
      )}
    </div>
  )
}

export function blankTID() {
  return {
    enabled: false,
    callsign: '',
    displayName: '',
    description: '',
    lastKnown: '',
    photoStoragePath: null,
    photoSignedUrl: null,
  }
}

// Validates T-ID form state; returns error string or null.
export function validateTID(tid) {
  if (!tid?.enabled) return null
  if (!tid.callsign.trim()) return 'T-ID: callsign is required.'
  if (!tid.description.trim()) return 'T-ID: description is required.'
  return null
}

// Builds the dossier_entries insert payload from T-ID state.
// eventId, parentType, parentId, authorId must come from the caller.
export function buildTIDPayload(tid, { eventId, parentType, parentId, authorId }) {
  if (!tid?.enabled) return null
  return {
    event_id:           eventId,
    parent_type:        parentType,
    parent_id:          parentId,
    entry_type:         't_id',
    author_id:          authorId,
    body:               tid.description.trim(),
    target_meta: {
      callsign:      tid.callsign.trim(),
      display_name:  tid.displayName.trim() || null,
      description:   tid.description.trim(),
      last_known:    tid.lastKnown.trim() || null,
    },
    photo_storage_path: tid.photoStoragePath || null,
  }
}

// ── Read mode ─────────────────────────────────────────────────────────────

// Dossier sepia style tokens (matches BountyDetail / FragoDetail).
const D = {
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body:    { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  ink:     { color: '#1f1d18' },
  ink3:    { color: '#7a7058' },
  stamp:   { color: '#a82a1f' },
}

// Renders the T-ID section inside a dossier detail page.
// Fetches entries from dossier_entries_view; generates signed URLs on demand.
export function TIDDisplaySection({ parentType, parentId, eventId, sectionNum }) {
  const [entries, setEntries] = useState(null) // null = loading, [] = none
  const [signedUrls, setSignedUrls] = useState({})

  useEffect(() => {
    if (!parentId || !eventId) return
    fetchEntries()
  }, [parentId, eventId])

  const fetchEntries = async () => {
    const { data } = await supabase
      .from('dossier_entries_view')
      .select('*')
      .eq('parent_type', parentType)
      .eq('parent_id', parentId)
      .eq('entry_type', 't_id')
      .order('created_at', { ascending: true })
    setEntries(data || [])

    // Generate signed URLs for entries where caller is authorized.
    const urls = {}
    for (const e of (data || [])) {
      if (e.photo_storage_path) {
        const { data: signed } = await supabase.storage
          .from('event-media')
          .createSignedUrl(e.photo_storage_path, 3600)
        if (signed?.signedUrl) urls[e.id] = signed.signedUrl
      }
    }
    setSignedUrls(urls)
  }

  if (entries === null) return null
  if (entries.length === 0) return null

  return (
    <>
      <div style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#4a4636', borderBottom: '1px solid #cbb98c', paddingBottom: 3, marginBottom: 8, marginTop: 14 }}>
        § {String(sectionNum).padStart(2, '0')} — TARGET IDENTIFICATION
      </div>

      {entries.map(entry => {
        const meta = entry.target_meta || {}
        const signedUrl = signedUrls[entry.id] || null
        const isRedacted = entry.photo_is_redacted

        return (
          <div key={entry.id} style={{ background: '#e8dec3', border: '1px solid rgba(120,100,60,.35)', padding: 12, marginBottom: 8, display: 'flex', gap: 12 }}>
            {/* Photo column */}
            <div style={{ flexShrink: 0 }}>
              {signedUrl ? (
                <div style={{ width: 72, height: 90, border: '1px solid #7a7058', overflow: 'hidden' }}>
                  <img src={signedUrl} alt="Target ID" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              ) : isRedacted ? (
                <div style={{ width: 72, height: 90, background: '#1a1a1a', border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <span style={{ ...D.display, fontSize: 7, color: '#444', letterSpacing: 2, transform: 'rotate(-30deg)', position: 'absolute' }}>CLASSIFIED</span>
                </div>
              ) : null}
            </div>

            {/* Text column */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...D.display, fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', color: '#1f1d18', marginBottom: 5 }}>
                {meta.callsign || '—'}
                {meta.display_name && <span style={{ ...D.body, fontSize: 11, color: '#4a4636', marginLeft: 8, fontWeight: 400 }}>· {meta.display_name}</span>}
              </div>
              {meta.description && (
                <div style={{ fontSize: 11, color: '#4a4636', lineHeight: 1.5, marginBottom: 3 }}>
                  {meta.description}
                </div>
              )}
              {meta.last_known && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginTop: 4 }}>
                  <span style={{ ...D.ink3, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: 52, flexShrink: 0 }}>LAST SEEN</span>
                  <span style={{ fontSize: 11, color: '#4a4636' }}>{meta.last_known}</span>
                </div>
              )}
              <div style={{ fontSize: 9, color: '#7a7058', letterSpacing: '0.06em', marginTop: 6 }}>
                FILED {new Date(entry.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
